import { useCallback, useState } from 'react';
import { analytics, Runtime } from '@silvertours/front-entities';
import type { http } from '@silvertours/front-shared';
import type { FormEvent } from 'react';
import { isOneWay } from '../../SearchForm';
import { useSearchContext } from '../context';
import { isFormError } from '../context/types';
import { getOptionsParam } from '../utils';
import { useSession } from './useSession';
import { matchErrors, useSubmitHandler } from './useSubmitHandler';
import { getDays } from '../../SearchWizard/datetime';

const useSearchFormSubmit = () => {
  const { setError, state } = useSearchContext();
  const submitHandler = useSubmitHandler();
  const { trackClickTimestamp, removeClickTimestamp } = useSession();
  const { isRatgeberPage } = Runtime.usePath();

  const [loading, setLoading] = useState(false);
  const [checkedOptions, setCheckedOptions] = useState<string[]>([]);

  const handleOptionsChange = (optionsId: string) => {
    setCheckedOptions(prev =>
      prev.includes(optionsId)
        ? prev.filter(key => key !== optionsId)
        : [...prev, optionsId],
    );
  };

  const onError = useCallback(
    (error: http.AxiosError) => {
      setLoading(false);
      removeClickTimestamp();

      matchErrors(error).forEach(({ type: kind, message }) => {
        if (isFormError(kind)) {
          setError({ kind, message });
          analytics.gtm.trackSearchError(kind);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const parseDate = (date: string | undefined) =>
    date ? new Date(date) : new Date();

  const handleSearchSubmit = async () => {
    setLoading(true);
    trackClickTimestamp();

    await submitHandler(
      state,
      getOptionsParam(checkedOptions),
      () => {
        setLoading(false);
        analytics.gtm.trackSearchSubmit(isOneWay(state));
        analytics.generic.trackStartSearch(
          state,
          getDays(parseDate(state?.depDate), parseDate(state?.destDate)),
          isOneWay(state),
          isRatgeberPage ? 'Ratgeber Pages' : undefined,
        );
      },
      onError,
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) {
      return;
    }
    handleSearchSubmit();
  };

  return {
    loading,
    checkedOptions,
    handleOptionsChange,
    handleSearchSubmit,
    handleSubmit,
  };
};

export { useSearchFormSubmit };
